import { Component, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Permission, PermissionService, SuperAdminAccessService } from '@vdms-hq/auth';
import { QUARANTINE_ALL_ASSET_TYPES, SupplierPatchRequest } from '@vdms-hq/api-contract';
import { QuarantineService } from '@vdms-hq/quarantine';
import { ApiFieldsSourceService, SelectorComponent, SelectorSourceType } from '@vdms-hq/selectors';
import {
  capitalize,
  DialogResponse,
  E2eIdDirective,
  emailsArrayValidator,
  SelectOption,
  ValueFormat,
} from '@vdms-hq/shared';
import { isEqual } from 'lodash';
import {
  BreadCrumb,
  ButtonComponent,
  DialogPageBackHandler,
  DialogPageWrapperComponent,
  DialogWrapperComponent,
  FormInputChipsComponent,
  FormInputSelectComponent,
  FormInputTextComponent,
  FormInputToggleComponent,
  FormSectionComponent,
  SectionWrapperComponent,
  LoaderComponent,
  PreventLeaveConfirmationDialogComponent,
  TileSelectableConfig,
  UIConfirmationDialogService,
} from '@vdms-hq/ui';
import { AddUserDialogComponent, UserAddData, UsersShareAddDataSource } from '@vdms-hq/users-core';
import { SelectableWrapperConfig, UiSelectableTilesComponent } from '@vdms-hq/selectable-tiles-wrapper';
import { DynamicFilterInput } from '@vdms-hq/dynamic-filters';
import { FilterType } from '@vdms-hq/fields';
import { BehaviorSubject, EMPTY, filter, switchMap, take, tap } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { SupplierDialogData } from './supplier-dialog.data';
import {
  FieldValuesUpsertDialogComponent,
  FieldValuesUpsertDialogInput,
  FieldValuesUpsertDialogResponse,
} from '@vdms-hq/fields-admin';
import { TranslatePipe } from '@ngx-translate/core';
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';

/**
 * What editing a supplier closes with: the fields the operator actually touched, ready to PATCH.
 * `configChanged` is a signal to the caller rather than part of the request -- quarantine rules go
 * to their own endpoint.
 */
export interface SupplierEditDialogResult extends SupplierPatchRequest {
  configChanged: boolean;
}

/**
 * Create or edit a supplier. Editing renders as a dialog page covering the suppliers table, with the
 * assigned users edited alongside the details in one save (#16593); creating keeps the original
 * centred modal, and shows no users section. `data.asPage` picks the frame.
 *
 * Both frames share one body through an `ng-template`, which constrains the template: `[formGroup]`
 * must live INSIDE that template. An `ngTemplateOutlet` view resolves DI from where the template is
 * declared rather than where it is inserted, so a form group at the insertion point is invisible to
 * the controls inside — every one of them throws NG01050 and renders empty.
 */
@Component({
  selector: 'vdms-hq-suppliers-create-edit-dialog',
  templateUrl: './suppliers-create-edit-dialog.component.html',
  styleUrls: ['./suppliers-create-edit-dialog.component.scss'],
  standalone: true,
  imports: [
    DialogWrapperComponent,
    DialogPageWrapperComponent,
    FormsModule,
    ReactiveFormsModule,
    FormSectionComponent,
    SectionWrapperComponent,
    FormInputTextComponent,
    FormInputChipsComponent,
    FormInputSelectComponent,
    SelectorComponent,
    LoaderComponent,
    FormInputToggleComponent,
    UiSelectableTilesComponent,
    ButtonComponent,
    AsyncPipe,
    NgTemplateOutlet,
    TranslatePipe,
    MatIconModule,
    E2eIdDirective,
  ],
})
export class SuppliersCreateEditDialogComponent
  extends PreventLeaveConfirmationDialogComponent<SuppliersCreateEditDialogComponent>
  implements OnInit, DialogPageBackHandler
{
  public quarantineService = inject(QuarantineService);
  public permissionService = inject(PermissionService);
  public superAdminAccessService = inject(SuperAdminAccessService);
  private fieldsSourceService = inject(ApiFieldsSourceService);
  private matDialog = inject(MatDialog);
  private suppliersEditDialogRef = inject(MatDialogRef<SuppliersCreateEditDialogComponent>);
  public data: SupplierDialogData = inject(MAT_DIALOG_DATA);
  private suppliersConfirmationDialog = inject(UIConfirmationDialogService);

  protected readonly Permission = Permission;

  isLoading$ = new BehaviorSubject(false);
  isCreating$ = new BehaviorSubject(false);

  dialogTitle!: string;

  /** Dialog page only: the trail, plus the supplier's name on the second line. */
  breadcrumbs: BreadCrumb[] = [];

  /**
   * True for a legacy record stored without any email address — the QC Project Onboarding
   * wizard used to create suppliers that way (#1932). Such a record must stay editable, so
   * the emails control drops `required` and the template explains why the field is empty.
   */
  protected readonly missingEmail = this.data.edit && !this.data?.supplier?.emails?.length;

  supplierForm = new FormGroup({
    name: new FormControl<string | null>(this.data?.supplier?.name ?? this.data?.label ?? null, Validators.required),
    // Email stays required when creating a supplier, and when editing one that already has an
    // address (so it cannot be cleared). Only the legacy records above are exempt — otherwise
    // the form is invalid from the moment it opens and Save can never be pressed.
    emails: new FormControl<string[] | null>(this.data?.supplier?.emails ?? null, [
      ...(this.missingEmail ? [] : [Validators.required]),
      emailsArrayValidator(true),
    ]),
    facility_of_origin: new FormControl<string | null>(
      this.data?.supplier?.facility_of_origin ?? null,
      Validators.required,
    ),
    quarantine: new FormControl<string[]>([], { nonNullable: true }),
    auto_approve_delivery_packs: new FormControl<boolean>(this.data?.supplier?.auto_approve_delivery_packs ?? false),
    notifies_librarian_group: new FormControl<boolean>(this.data?.supplier?.notifies_librarian_group ?? false),
    is_global: new FormControl<boolean>({ value: this.data?.supplier?.is_global ?? false, disabled: this.data.edit }),
    /**
     * Assigned user uuids. A member of this group rather than a control beside it, so the Save gate
     * (`hasChanges`), the dirty state and the unsaved-changes guard all see user edits without a
     * special case — they are all driven off this form.
     */
    users: new FormControl<string[]>(this.#sortedUserIds(this.data?.supplier?.users?.map(({ uuid }) => uuid)), {
      nonNullable: true,
    }),
  });

  /** Snapshot of the form taken once initial (incl. async-loaded quarantine) data is in place. */
  #initialFormValue = this.supplierForm.getRawValue();

  public usersDataSource = inject(UsersShareAddDataSource);

  readonly usersFiltersConfig: DynamicFilterInput[] = [
    {
      id: 'keyword',
      label: 'Search by name or email',
      format: ValueFormat.AS_IS,
      filters: {
        objectPath: 'text',
        validFormat: 'keyword',
        type: FilterType.MASTER_TEXT,
      },
      scope: ['other-users'],
    },
  ];

  readonly usersTilesConfig: TileSelectableConfig<UserAddData> = {
    label: 'label',
    key: 'key',
    icon: 'person',
    metadata: [
      {
        label: 'Email',
        valuePath: 'email',
        viewFormat: { modifiers: { truncateCharacters: 50 } },
      },
    ],
  };

  usersSelectableConfig: SelectableWrapperConfig<UserAddData> = {
    fieldsConfig: this.usersFiltersConfig,
    tileConfig: this.usersTilesConfig,
    selectedList: {
      title: 'Assigned users',
      icon: 'group',
      // Seeded from the supplier so the panel is populated before the list has loaded.
      data: this.data?.supplier?.users?.map(({ uuid, name }) => ({ uuid, name })) ?? [],
    },
    searchView: {
      placeholder: 'Type name or email',
      label: 'Search users',
      icon: 'manage_search',
      // Opens on the list, not the search box: assigning people is browsing, and the list carries
      // the filter and paginator with it.
      showSearch: false,
    },
    datasourceTitle: 'All users',
    datasourceIcon: 'group_add',
    emptyMessage: 'No users found',
    loadingText: 'Loading users...',
    keepSelectedListState: true,
    // Offered when a search finds nobody, which is when an admin discovers the person is missing.
    // The old assigned-users dialog reached the same dialog through the users selector.
    hasEmptyActionButton: () => this.#canCreateUsers,
    emptyActionButtonText: 'common.global.create_user',
  };

  #canCreateUsers = false;

  sourceType = SelectorSourceType;
  quarantineSelectOptions: SelectOption[] = QUARANTINE_ALL_ASSET_TYPES.map((type) => ({
    key: type,
    label: type === 'subtitle' ? 'Timed text' : capitalize(type),
  }));

  ngOnInit() {
    // Reset on entry, not on an exit path: this editor has four (save, cancel, breadcrumb, back).
    if (this.data.asPage) {
      this.usersDataSource.refresh();
    }
    this.dialogTitle =
      this.data && this.data.edit ? 'pages.suppliers.edit_supplier' : 'pages.suppliers.create_supplier';
    this.breadcrumbs = [...(this.data.breadcrumbs ?? []), { name: 'common.global.edit' }];
    this.permissionService
      .verifyWithOwnedPermissions$([Permission.CREATE_USERS])
      .pipe(take(1))
      .subscribe((canCreate) => (this.#canCreateUsers = canCreate));
    this.isLoading$.next(true);
    this.quarantineService.canManageQuarantineConfigs$
      .pipe(
        take(1),
        tap((canManageQuarantineConfigs) => {
          if (!canManageQuarantineConfigs) {
            this.supplierForm.get('quarantine')?.disable();
          }
        }),
        switchMap((canManageQuarantineConfigs) => {
          if (!canManageQuarantineConfigs || !this.data.edit) {
            return EMPTY;
          }

          return this.quarantineService.existingClientSupplierQuarantineConfig$(this.data.supplier?.uuid).pipe(take(1));
        }),
      )
      .subscribe({
        next: (quarantine) => {
          if (!quarantine) {
            return;
          }
          const rules: string[] = Object.keys(quarantine.rules ?? {}).filter(
            (key) =>
              (
                quarantine.rules as {
                  [key: string]: boolean;
                }
              )[key],
          );
          this.supplierForm.patchValue({
            quarantine: rules,
          });
        },
        complete: () => {
          this.isLoading$.next(false);
          // Re-snapshot now that any async-loaded quarantine rules are applied, so loading
          // the existing supplier doesn't count as a change.
          this.#initialFormValue = this.supplierForm.getRawValue();
        },
      });
  }

  /** The heading beneath the trail: which supplier is being edited. */
  get pageTitle(): string {
    return this.data.supplier?.name ?? this.dialogTitle;
  }

  get hasChanges(): boolean {
    return !isEqual(this.#normalizeForm(this.supplierForm.getRawValue()), this.#normalizeForm(this.#initialFormValue));
  }

  /**
   * Treats empty string and null/undefined as the same "empty" value, so filling an
   * initially-empty field and then clearing it is not seen as a change.
   */
  #normalizeForm(value: Record<string, unknown>): Record<string, unknown> {
    return Object.fromEntries(Object.entries(value).map(([key, val]) => [key, val === '' || val == null ? null : val]));
  }

  /** Sorted, so picking the same people in a different order is not read as a change. */
  #sortedUserIds(ids?: string[]): string[] {
    return [...(ids ?? [])].sort();
  }

  updateSelection(userIds: string[]): void {
    const users = this.supplierForm.controls.users;
    users.setValue(this.#sortedUserIds(userIds));
    // The selector does not drive the control itself, so the dirty flag has to be set here or the
    // leave guard would let user edits go without asking.
    users.markAsDirty();
  }

  public onSubmit(): void {
    if (this.supplierForm.invalid) {
      return;
    }

    if (this.data.edit) {
      if (this.data.supplier?.have_aspera_sub_keys) {
        if (this.supplierForm.controls.facility_of_origin.dirty) {
          this.#showConfirmationDialog();
        }
        this.#closeAfterEdit();
      } else {
        this.#closeAfterEdit();
      }
    }

    if (!this.data.edit) {
      this.suppliersEditDialogRef.close({
        uuid: uuidv4(),
        ...this.supplierForm.value,
      });
    }
  }

  public cancel(): void {
    this.suppliersEditDialogRef.close();
  }

  /**
   * Creates the user the admin was searching for and leaves them showing in All users, filtered to
   * their address, for the admin to click and assign.
   *
   * Deliberately not assigned here. Doing both -- adding the uuid to the selection and appending to
   * the added list by hand -- fell apart on the reload that followed: the panel rebuilds its added
   * list from the page the datasource has loaded, the brand-new user was not on it, so Assigned
   * users came back empty while the uuid stayed selected. The tile then looked already-clicked with
   * nothing to show for it. Letting the operator click does the whole thing through the component,
   * including the NEW ADDED pill.
   */
  createNewUser(): void {
    const keyword = this.usersDataSource.filters.controls.keyword;
    this.matDialog
      .open(AddUserDialogComponent, { data: { email: keyword.value } })
      .afterClosed()
      .pipe(
        take(1),
        filter((response) => response?.status === DialogResponse.OK && !!response?.user),
      )
      .subscribe(({ user }) => {
        keyword.setValue(user.email ?? user.name ?? '');
        keyword.markAsDirty();
      });
  }

  /** Go back, an earlier breadcrumb and the browser back button all land here. */
  requestClose(): Promise<void> {
    return this.confirmAndClose();
  }

  #showConfirmationDialog() {
    this.suppliersConfirmationDialog
      .openDelete({
        title: 'pages.suppliers.confirmation.title',
        message: 'pages.suppliers.confirmation.message',
        okAction: {
          label: 'pages.suppliers.confirmation.ok_action',
          color: 'primary',
        },
        abortAction: {
          label: 'pages.suppliers.confirmation.cancel_action',
          color: 'warn',
        },
      })
      .pipe(
        take(1),
        filter(Boolean),
        switchMap(() => {
          this.#closeAfterEdit();
          return EMPTY;
        }),
      )
      .subscribe();
  }

  #closeAfterEdit() {
    this.suppliersEditDialogRef.close({
      uuid: this.data.supplier?.uuid,
      configChanged: this.supplierForm.controls.quarantine.dirty,
      ...this.#getDirtyValues(),
    });
  }

  #getDirtyValues(): Partial<typeof this.supplierForm.value> {
    return Object.fromEntries(
      Object.entries(this.supplierForm.controls)
        .filter(([, control]) => control.dirty)
        .map(([key, control]) => [key, control.value]),
    );
  }

  openCreateFacilityOfOrigin(label: string) {
    this.fieldsSourceService
      .getTypeByName(this.sourceType.FACILITY_OF_ORIGIN)
      .pipe(
        // Take the type once: `getTypeByName` is backed by the shared, re-emitting `types$`
        // stream, which refreshes after the upsert dialog saves a new value. Without this the
        // still-subscribed `switchMap` would reopen the dialog on that refresh (save loop).
        take(1),
        switchMap((type) =>
          this.matDialog
            .open<FieldValuesUpsertDialogComponent, FieldValuesUpsertDialogInput, FieldValuesUpsertDialogResponse>(
              FieldValuesUpsertDialogComponent,
              {
                data: {
                  createOptions: {
                    type,
                    label,
                  },
                },
              },
            )
            .afterClosed(),
        ),
        filter((data) => !!data),
      )
      .subscribe({
        error: (err: Error) => {
          this.isCreating$.next(false);
          throw err;
        },
        next: (response) => {
          this.supplierForm.patchValue({ facility_of_origin: response?.field.label });
          this.isCreating$.next(false);
        },
      });
  }

  override canClose() {
    return this.supplierForm.pristine;
  }
}
